<template>
    <page :tabs="tabs" :busy="busy">
        <grid
            ref="grid"
            :columnDefs="columnDefs"
            :fetchCallback="fetchCallback"
            :pinnedBottomRowData="pinnedBottomRowData"
            gridTheme="ag-theme-alpine-report"
            :getRowStyle="rowStyle"
            :isSearch="false"
            :buttonToolbarClass="['align-items-start flex-md-nowrap']"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            ></date-range-picker>
            <responsive-dropdown :items="statusOptions" variant="primary" class="mb-0 mr-1"
                >{{ selectedStatusLabel }}
            </responsive-dropdown>
            <form-search-field
                v-if="userIsAdmin"
                :options="subscribersList"
                @input="searchSubscribers"
                :busy="busy"
                :value="subscriberSearch"
                @select="selectSubscriber"
                placeholder="Filter By Subscriber"
            />
        </grid>
    </page>
</template>

<script>
import Page from '../../generic/page/page.vue';
import { format } from 'date-fns';
import debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';
import API from '../../../api';
import { GUEST_VERIFICATIONS_REPORT_ENDPOINT, ADMIN_SMS_LOG_REPORT_ENDPOINT } from '../../../endpoints';
import DateRangePicker, {
    LAST_MONTH,
    LAST_WEEK,
    LAST_YEAR,
    nameToRange,
    THIS_MONTH,
    THIS_WEEK,
    THIS_YEAR,
    TODAY,
    YESTERDAY,
    ALL_DATES,
} from '../../generic/date-range-picker/date-range-picker.vue';
import Grid from '../../generic/grid/grid.vue';
import FormSearchField from '../../generic/form-search-field/form-search-field.vue';
import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';

export default {
    name: 'GuestVerificationAttemptReport',
    components: {
        Page,
        Grid,
        DateRangePicker,
        FormSearchField,
        ResponsiveDropdown,
    },
    data() {
        return {
            busy: false,
            tabs: [
                {
                    title: 'Guest Verification Report',
                    active: true,
                },
            ],
            dateRange: nameToRange(THIS_MONTH),
            dateRequestFormat: 'yyyy-MM-dd',
            predefinedRanges: [
                LAST_MONTH,
                LAST_WEEK,
                LAST_YEAR,
                THIS_MONTH,
                THIS_WEEK,
                THIS_YEAR,
                TODAY,
                YESTERDAY,
                ALL_DATES,
            ],
            totalAmount: 0,
            offset: 0,
            limit: 20,
            sort: 'created',
            desc: true,
            rowStyle: null,
            subscribersList: [],
            subscriberSearch: '',
            selectedSubscriber: null,
            selectedStatus: null,
            selectedStatusLabel: 'All Attempts',
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol', 'userIsAdmin']),
        pinnedBottomRowData() {
            return [
                {
                    amount: this.totalAmount,
                },
            ];
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                return API.get(`${GUEST_VERIFICATIONS_REPORT_ENDPOINT()}grid/`, {
                    params: {
                        offset,
                        limit,
                        sort,
                        desc,
                        search,
                        start_date: this.startDateRequest,
                        end_date: this.endDateRequest,
                        subscriber: this.selectedSubscriber,
                        status: this.selectedStatus,
                    },
                });
            };
        },
        columnDefs() {
            return [
                {
                    field: 'created',
                    headerName: 'Timestamp',
                    type: 'dateTimeColumn',
                    minWidth: 160,
                    maxWidth: 160,
                },
                {
                    field: 'status',
                    headerName: 'Status',
                    cellRenderer: ({ data, node }) =>
                        !node.rowPinned
                            ? `<span class="${data.status === 'SUCCESS' ? 'text-success' : 'text-danger'}">${
                                  data.status
                              }</span>`
                            : '',
                    minWidth: 100,
                    maxWidth: 100,
                },
                {
                    field: 'subscriber',
                    headerName: 'Subscriber',
                    cellRenderer: ({ data, node }) =>
                        !node.rowPinned
                            ? `<a href="/app/admin/user/details/${data.subscriber}">${data.subscriber}</a>`
                            : '',
                    hide: !this.userIsAdmin,
                    maxWidth: 250,
                },

                {
                    field: 'guest_email',
                    headerName: 'Guest Email',
                    sortable: false,
                },
                {
                    field: 'guest_name',
                    headerName: 'Guest Name',
                    sortable: false,
                },
                {
                    field: 'meta_reservation',
                    headerName: 'Invoice #',
                    minWidth: 100,
                    maxWidth: 120,
                },
                {
                    field: 'reason',
                    headerName: 'Reason',
                    minWidth: 450,
                },
                {
                    field: 'amount',
                    headerName: 'Amount',
                    type: 'amountColumn',
                    sortable: false,
                    minWidth: 90,
                    maxWidth: 90,
                },
            ];
        },
        statusOptions() {
            const statuses = [
                {
                    value: null,
                    text: 'All Attempts',
                },
                {
                    value: 'FAILURE',
                    text: 'Failure Attempts',
                },
                {
                    value: 'SUCCESS',
                    text: 'Success Attempts',
                },
            ];
            return statuses.map((item) => ({
                label: item.text,
                click: () => {
                    this.selectedStatusLabel = item.text;
                    this.selectedStatus = item.value;
                    this.refresh();
                },
            }));
        },
    },
    mounted() {
        this.fetchTotals();
        this.rowStyle = (params) => {
            if (params.node.rowPinned) {
                return { 'font-weight': 'bold' };
            }
        };
        if (this.userIsAdmin) {
            this.fetchSubscribers();
        }
    },
    methods: {
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.refresh();
        },
        refresh() {
            this.fetchTotals();
            this.$refs.grid.refreshDataSource();
        },
        async fetchTotals() {
            try {
                this.busy = true;
                const { data } = await API.get(`${GUEST_VERIFICATIONS_REPORT_ENDPOINT()}totals/`, {
                    params: {
                        start_date: this.startDateRequest,
                        end_date: this.endDateRequest,
                        subscriber: this.selectedSubscriber,
                    },
                });
                this.totalAmount = data.total_amount;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            } finally {
                this.busy = false;
            }
        },
        async fetchSubscribers() {
            try {
                this.busy = true;
                const { data } = await API.get(`${ADMIN_SMS_LOG_REPORT_ENDPOINT()}subscribers/`, {
                    params: {
                        search: this.subscriberSearch,
                    },
                });

                this.subscribersList = data.map(({ user }) => ({
                    text: `${user.id} - ${user.email}`,
                    value: user.id,
                }));
            } catch (e) {
                this.errorToast({ text: 'Could not fetch subscribers' });
            } finally {
                this.busy = false;
            }
        },
        selectSubscriber(text, value) {
            this.subscriberSearch = text;
            this.selectedSubscriber = value;
            this.subscribersList = [];
            this.refresh();
        },
        searchSubscribers: debounce(function (value) {
            this.subscriberSearch = value;
            this.selectedSubscriber = '';
            if (value === '') {
                this.refresh();
            } else {
                this.fetchSubscribers();
            }
        }, 500),
    },
};
</script>

<style>
</style>